import { Button } from '@/components/ui/button';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import {
  AdvanceFilter,
  FilterTypeOperatorMap,
  AdvanceFilterOperator,
} from '@/types/types';
import { ChevronDown, ChevronUp, Plus, SlidersHorizontal, Trash2, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { FILTER_OPERATOR_OPTIONS, US_STATES_OPTIONS } from '@/utils/constant';
import { v4 as uuid } from 'uuid';
import { FormFieldSelect, Option } from '@/components/FormFieldSelect';
import { FormProvider, useForm } from 'react-hook-form';
import { AdvanceFilterValueField } from '@/components/AdvanceFilterComponents';
import { convertISODateToDDMMYYYY } from '@/lib/utils';
import {
  canAddDdiAdvanceFilter,
  ddiAdvanceFilterConfig,
  ddiFilterRowHasErrors,
  getAvailableDdiAdvanceFieldOptions,
  getDdiAllowedOperators,
  validateDdiFilterRow,
  type DdiFilterRowErrors,
} from './ddiSearchConfig';

type SelectedFilterRow = {
  id: string;
  operators?: AdvanceFilterOperator[];
  filter?: AdvanceFilter;
  isExpanded?: boolean;
};

type DDIAdvanceFiltersProps = {
  onClose: () => void;
  filters: AdvanceFilter[];
  setFilters: (filters: AdvanceFilter[]) => void;
  ddiListViewFilters: string[];
  open: boolean;
  ddiTypeOptions: Option[];
  ddiSubtypeOptions: Option[];
  caseTypeOptions: Option[];
  caseSubtypeOptions: Option[];
};

export const DDIAdvanceFilters = ({
  onClose,
  filters,
  setFilters,
  ddiListViewFilters,
  open,
  ddiTypeOptions,
  ddiSubtypeOptions,
  caseTypeOptions,
  caseSubtypeOptions,
}: DDIAdvanceFiltersProps) => {
  const form = useForm({ mode: 'onChange' });

  const fieldDefaultOptions: Record<string, Option[]> = useMemo(
    () => ({
      ddiSubtype: ddiSubtypeOptions,
      caseType: caseTypeOptions,
      caseSubType: caseSubtypeOptions,
      ddiType: ddiTypeOptions,
      state: US_STATES_OPTIONS,
    }),
    [ddiSubtypeOptions, caseTypeOptions, caseSubtypeOptions, ddiTypeOptions]
  );

  const enumOptionsByField = useMemo(() => {
    const map: Record<string, string[]> = {};
    for (const [field, options] of Object.entries(fieldDefaultOptions)) {
      map[field] = options.map((o) => o.value);
    }
    return map;
  }, [fieldDefaultOptions]);

  const [selectedFilters, setSelectedFilter] = useState<SelectedFilterRow[]>([]);
  const [attemptedApply, setAttemptedApply] = useState(false);

  useEffect(() => {
    if (filters) {
      setSelectedFilter(
        filters
          .filter((filter) => filter.field && !ddiListViewFilters.includes(filter.field))
          .map((filter) => {
            const config = ddiAdvanceFilterConfig.find((c) => c.value === filter.field);
            return {
              id: uuid(),
              filter,
              isExpanded: false,
              operators: config
                ? getDdiAllowedOperators(config)
                : filter.fieldType
                  ? FilterTypeOperatorMap[filter.fieldType]
                  : [],
            };
          })
      );
      setAttemptedApply(false);
    }
  }, [filters, ddiListViewFilters]);

  const rowErrors = useMemo(() => {
    const next: Record<string, DdiFilterRowErrors> = {};
    for (const row of selectedFilters) {
      // Brand-new blank rows (no field yet) are not validated until Apply is attempted,
      // so Add Filter does not immediately disable Apply.
      const isPristineDraft = !row.filter?.field;
      if (isPristineDraft && !attemptedApply) continue;

      const errors = validateDdiFilterRow(row.filter, { enumOptionsByField });
      if (ddiFilterRowHasErrors(errors)) {
        next[row.id] = errors;
      }
    }
    return next;
  }, [selectedFilters, enumOptionsByField, attemptedApply]);

  const hasValidationErrors = Object.keys(rowErrors).length > 0;

  const onDeleteFilter = (id: string) => setSelectedFilter((prev) => prev.filter((f) => f.id !== id));

  const toggleExpand = (id: string, expand: boolean) =>
    setSelectedFilter((prev) => prev.map((f) => (f.id === id ? { ...f, isExpanded: expand } : f)));

  const updateRowFilter = (id: string, updater: (row: SelectedFilterRow) => SelectedFilterRow) => {
    setSelectedFilter((prev) => prev.map((sf) => (sf.id === id ? updater(sf) : sf)));
  };

  const handleApply = () => {
    setAttemptedApply(true);

    const applyErrors: Record<string, DdiFilterRowErrors> = {};
    for (const row of selectedFilters) {
      const errors = validateDdiFilterRow(row.filter, { enumOptionsByField });
      if (ddiFilterRowHasErrors(errors)) {
        applyErrors[row.id] = errors;
      }
    }
    if (Object.keys(applyErrors).length > 0) return;

    setFilters(
      selectedFilters
        .filter((sf): sf is SelectedFilterRow & { filter: AdvanceFilter } => !!sf.filter)
        .map((sf) => ({
          ...sf.filter,
          value: typeof sf.filter.value === 'string' ? sf.filter.value.trim() : sf.filter.value,
        }))
        .concat(filters.filter((f) => f.field && ddiListViewFilters.includes(f.field)))
    );
    setAttemptedApply(false);
    onClose();
  };

  const formatCollapsedValue = (row: SelectedFilterRow): string => {
    const config = ddiAdvanceFilterConfig.find((c) => c.value === row.filter?.field);
    if (!row.filter?.value || !row.filter.field || !config) return '';
    if (config.inputType === 'INPUT') return row.filter.value;
    if (config.inputType === 'DATE') return convertISODateToDDMMYYYY(row.filter.value);
    if (config.inputType === 'SELECT') {
      return (
        fieldDefaultOptions[row.filter.field]?.find((opt) => opt.value === row.filter?.value)?.label ??
        row.filter.value
      );
    }
    return '';
  };

  return (
    <Sheet modal={true} open={open}>
      <SheetContent
        hideCloseButton={true}
        className="w-[80vw] md:w-[500px] scrollbar-hide bg-white px-4 py-7 flex flex-col gap-6"
        side="right"
        tabIndex={-1}
      >
        <div className="flex w-full">
          <div className="w-full">
            <div className="flex justify-between">
              <div className="text-[20px]">Advance Search</div>
              <div className="border-[1px] p-[6px] rounded-md cursor-pointer" onClick={onClose}>
                <X color="#676767" className="h-[20px] w-[20px]" />
              </div>
            </div>
            <hr className="mt-4 border-[#CAC8C8]" />
            <div className="mt-5 flex justify-between items-center">
              <div className="flex gap-2 items-center">
                <SlidersHorizontal className="w-5 h-5 text-primary" />
                <span className="text-[16px] font-medium text-primary">Advance Search Filters</span>
              </div>
              {canAddDdiAdvanceFilter(selectedFilters.map((sf) => sf.filter?.field)) && (
                <Button
                  className="h-8 flex gap-2"
                  onClick={() => {
                    setSelectedFilter((prev) => [...prev, { id: uuid(), isExpanded: true, operators: [] }]);
                  }}
                >
                  <Plus className="w-4 h-4" />
                  <span className="text-[14px]">Add Filter</span>
                </Button>
              )}
            </div>
          </div>
        </div>

        <FormProvider {...form}>
          <div className="flex-1 flex flex-col overflow-y-auto gap-6 custom-scrollbar">
            {selectedFilters.map((selectedFilter) => {
              const config = ddiAdvanceFilterConfig.find((c) => c.value === selectedFilter.filter?.field);
              const errors = rowErrors[selectedFilter.id];
              return (
                <div
                  key={selectedFilter.id}
                  className={`p-3 flex flex-col gap-6 border-[1px] rounded-xl border-[#969696] relative ${selectedFilter.isExpanded ? 'cursor-default' : 'cursor-pointer'}`}
                  onClick={() => !selectedFilter.isExpanded && toggleExpand(selectedFilter.id, true)}
                >
                  {selectedFilter.isExpanded ? (
                    <>
                      <div>
                        <FormFieldSelect
                          options={getAvailableDdiAdvanceFieldOptions(selectedFilters, selectedFilter.id)}
                          name={`${selectedFilter.id}_field`}
                          defaultValue={selectedFilter.filter?.field}
                          label="Search Fields"
                          onChange={(newValue) => {
                            if (!newValue) return;
                            const found = ddiAdvanceFilterConfig.find((c) => c.value === newValue);
                            if (!found) return;
                            const operators = getDdiAllowedOperators(found);
                            updateRowFilter(selectedFilter.id, (sf) => ({
                              ...sf,
                              operators,
                              filter: {
                                ...sf.filter,
                                field: newValue,
                                value: '',
                                fieldType: found.fieldType,
                                operator: operators[0],
                              },
                            }));
                          }}
                          placeholder="Select Search fields"
                        />
                        {errors?.field && <p className="text-xs text-[#D72222] mt-1">{errors.field}</p>}
                      </div>
                      {selectedFilter?.filter && (
                        <>
                          <div>
                            <FormFieldSelect
                              options={FILTER_OPERATOR_OPTIONS.filter((op) =>
                                selectedFilter.operators?.includes(op.value)
                              )}
                              name={`${selectedFilter.id}_operator`}
                              defaultValue={selectedFilter.filter?.operator}
                              label="Criteria"
                              placeholder="Select Criteria"
                              disabled={selectedFilter.operators?.length === 1}
                              onChange={(newValue) => {
                                updateRowFilter(selectedFilter.id, (sf) => ({
                                  ...sf,
                                  filter: {
                                    ...sf.filter,
                                    operator: newValue as AdvanceFilterOperator,
                                  },
                                }));
                              }}
                            />
                            {errors?.operator && (
                              <p className="text-xs text-[#D72222] mt-1">{errors.operator}</p>
                            )}
                          </div>
                          <div>
                            <AdvanceFilterValueField
                              inputType={config?.inputType ?? 'INPUT'}
                              name={`${selectedFilter.id}_value`}
                              value={selectedFilter.filter.value ?? ''}
                              options={
                                selectedFilter.filter.field
                                  ? (fieldDefaultOptions[selectedFilter.filter.field] ?? [])
                                  : []
                              }
                              onValueChange={(value) => {
                                updateRowFilter(selectedFilter.id, (sf) => ({
                                  ...sf,
                                  filter: { ...sf.filter, value: value ?? '' },
                                }));
                              }}
                            />
                            {errors?.value && <p className="text-xs text-[#D72222] mt-1">{errors.value}</p>}
                          </div>
                        </>
                      )}
                    </>
                  ) : (
                    <div className="flex flex-col">
                      <span className="text-sm">{config?.label}</span>
                      <div className="text-[#969696]">
                        <span className="text-sm">
                          {FILTER_OPERATOR_OPTIONS.find((op) => op.value === selectedFilter.filter?.operator)?.label}
                        </span>
                        {selectedFilter.filter?.value && selectedFilter.filter.field && (
                          <span className="break-words">
                            &nbsp;•&nbsp;
                            {formatCollapsedValue(selectedFilter)}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  <div className="absolute top-3 right-3 cursor-pointer flex items-center gap-3">
                    {selectedFilter.filter ? (
                      !selectedFilter.isExpanded ? (
                        <ChevronDown
                          className="h-5 w-5"
                          color="#969696"
                          onClick={() => toggleExpand(selectedFilter.id, true)}
                        />
                      ) : (
                        <ChevronUp
                          className="h-5 w-5"
                          color="#969696"
                          onClick={() => toggleExpand(selectedFilter.id, false)}
                        />
                      )
                    ) : null}
                    <Trash2 color="#D72222" className="h-5 w-5" onClick={() => onDeleteFilter(selectedFilter.id)} />
                  </div>
                </div>
              );
            })}
          </div>
        </FormProvider>

        <div className="bg-white sticky bottom-0">
          <div className="flex justify-end gap-3">
            {selectedFilters.length >= 1 && (
              <Button
                variant="outline"
                onClick={() => {
                  setSelectedFilter([]);
                  setAttemptedApply(false);
                  setFilters(filters.filter((f) => f.field && ddiListViewFilters.includes(f.field)));
                  onClose();
                }}
                className="px-4 py-3"
              >
                Clear All
              </Button>
            )}
            <Button
              className="px-7 py-3"
              disabled={hasValidationErrors}
              onClick={handleApply}
            >
              Apply
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
